import { Wordmark } from '../../components/Wordmark';
import { HeroSwoosh } from '../../components/HeroSwoosh';
import type { AuthorizationDecision } from '../../lib/nexus';

type Props = {
  decision: AuthorizationDecision;
  onSignOut: () => void;
  accent: string;
};

const REASON_COPY: Record<string, { title: string; body: string }> = {
  app_disabled: {
    title: 'Jones + Palmer tools is currently offline.',
    body: 'This app has been disabled in Nexus. Please contact your Jones + Palmer administrator.',
  },
  user_inactive: {
    title: 'Your account is not active.',
    body: 'Your J+P account is suspended or has been archived. Get in touch with your admin to restore access.',
  },
  no_matching_role: {
    title: 'You don’t have access yet.',
    body: 'Your account exists but doesn’t hold a role that grants access to Jones + Palmer tools. If you think this is wrong, ask a J+P admin.',
  },
  no_matching_permission: {
    title: 'You don’t have access yet.',
    body: 'Your account exists but doesn’t hold a permission that grants access to Jones + Palmer tools. If you think this is wrong, ask a J+P admin.',
  },
  user_not_entitled: {
    title: 'You haven’t been granted access.',
    body: 'This site requires explicit entitlement. Ask a J+P admin to grant your account access.',
  },
};

const FALLBACK = {
  title: 'Access denied.',
  body: 'Nexus declined this request. Please contact your Jones + Palmer administrator.',
};

export function AccessDenied({ decision, onSignOut, accent }: Props) {
  const copy = (decision.reason && REASON_COPY[decision.reason]) || FALLBACK;
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0a0a10',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="scene" />
      <div className="horizon" />
      <HeroSwoosh />
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,8,14,.55)' }} />
      <div style={{ position: 'absolute', top: 28, left: 36, zIndex: 2 }}>
        <Wordmark size={18} color="#fff" />
      </div>
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          display: 'grid',
          placeItems: 'center',
          minHeight: '100vh',
          padding: '80px 24px',
        }}
      >
        <div
          style={{
            width: 'min(100%, 520px)',
            background: 'var(--cream)',
            color: 'var(--ink)',
            borderRadius: 14,
            padding: '40px 40px 32px',
            boxShadow: '0 30px 80px rgba(0,0,0,.45)',
          }}
        >
          <p className="eyebrow" style={{ color: accent, marginBottom: 10 }}>
            Access
          </p>
          <h1 className="h2" style={{ fontSize: 32, lineHeight: 1.1 }}>
            {copy.title}
          </h1>
          <p className="body muted" style={{ marginTop: 14 }}>
            {copy.body}
          </p>
          {decision.email && (
            <p className="mono" style={{ marginTop: 18, color: 'rgba(14,14,14,.5)' }}>
              Signed in as {decision.email}
            </p>
          )}
          {(decision.reason || decision.message) && (
            <details
              style={{
                marginTop: 14,
                padding: '10px 12px',
                background: 'rgba(14,14,14,.04)',
                borderRadius: 6,
                fontSize: 12.5,
                color: 'rgba(14,14,14,.62)',
              }}
            >
              <summary style={{ cursor: 'pointer' }}>Diagnostic details</summary>
              <div style={{ marginTop: 8, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}>
                {decision.reason && (
                  <div>
                    reason: <code>{decision.reason}</code>
                  </div>
                )}
                {decision.message && (
                  <div style={{ marginTop: 4 }}>
                    message: <code>{decision.message}</code>
                  </div>
                )}
              </div>
            </details>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
            <button
              onClick={onSignOut}
              style={{
                border: 'none',
                cursor: 'pointer',
                background: 'var(--ink)',
                color: '#fff',
                padding: '12px 18px',
                borderRadius: 999,
                fontFamily: 'inherit',
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              Sign in with a different account
            </button>
            <a
              href="mailto:steven.tolley@jonesandpalmer.co.uk?subject=Access%20request"
              style={{
                border: '1px solid var(--ink)',
                cursor: 'pointer',
                background: 'transparent',
                color: 'var(--ink)',
                padding: '12px 18px',
                borderRadius: 999,
                fontFamily: 'inherit',
                fontWeight: 500,
                fontSize: 14,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Request access
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
